import { relations } from "drizzle-orm/_relations";
import { index, integer, pgTable, serial, unique } from "drizzle-orm/pg-core";
import { createSchemaFactory } from "drizzle-zod";
import { z } from "zod";

import { playerProfiles } from "./player-profiles";
import { timestamps } from "./shared";
import { tournamentInviteSources } from "./tournament-invite-sources";
import { tournaments } from "./tournaments";

const { createInsertSchema, createSelectSchema, createUpdateSchema } =
  createSchemaFactory({ zodInstance: z });

export const tournamentInvites = pgTable(
  "tournament_invites",
  {
    id: serial().primaryKey(),
    playerProfileId: integer()
      .notNull()
      .references(() => playerProfiles.id, { onDelete: "cascade" }),
    targetTournamentId: integer()
      .notNull()
      .references(() => tournaments.id, { onDelete: "cascade" }),
    sourceId: integer().references(() => tournamentInviteSources.id, {
      onDelete: "set null",
    }),
    ...timestamps,
  },
  (table) => [
    unique("tournament_invites_player_tournament_unique").on(
      table.playerProfileId,
      table.targetTournamentId,
    ),
    index("tournament_invites_target_idx").on(table.targetTournamentId),
    index("tournament_invites_source_id_idx").on(table.sourceId),
  ],
);

export const selectTournamentInviteSchema =
  createSelectSchema(tournamentInvites);
export const createTournamentInviteSchema = createInsertSchema(
  tournamentInvites,
).omit({
  id: true,
});
export const updateTournamentInviteSchema =
  createUpdateSchema(tournamentInvites);

export type TournamentInvite = z.infer<typeof selectTournamentInviteSchema>;
export type CreateTournamentInvite = z.infer<
  typeof createTournamentInviteSchema
>;
export type UpdateTournamentInvite = z.infer<
  typeof updateTournamentInviteSchema
>;

export const tournamentInvitesRelations = relations(
  tournamentInvites,
  ({ one }) => ({
    playerProfile: one(playerProfiles, {
      fields: [tournamentInvites.playerProfileId],
      references: [playerProfiles.id],
    }),
    targetTournament: one(tournaments, {
      fields: [tournamentInvites.targetTournamentId],
      references: [tournaments.id],
    }),
    source: one(tournamentInviteSources, {
      fields: [tournamentInvites.sourceId],
      references: [tournamentInviteSources.id],
    }),
  }),
);
